import { computed, effect, inject, Injectable, signal, Signal } from '@angular/core';
import { PermissionType } from '../../../app-core/models/permission/permission-type-enum';
import { AuthService } from '../../../app-core/services/auth.service';
import { ProductFamilyService } from '../../config/services/product-family.service';

/**
 * Decides whether the application may offer creating a new part.
 *
 * A part always belongs to a product family, so the entry point is hidden until at least one is
 * configured, even for a user who holds the permission.
 */
@Injectable({ providedIn: 'root' })
export class PartCreationService {
    private readonly authService = inject(AuthService);
    private readonly productFamilyService = inject(ProductFamilyService);

    private readonly hasProductFamily = signal(false);
    private readonly isAuthorized: Signal<boolean> = this.authService.isAuthorized([
        PermissionType.UpdatePartNumbers,
        PermissionType.UpdateMpns,
    ]);
    /** The product family count the authorization check above relies on needs this permission too. */
    private readonly canAccessProductFamilies: Signal<boolean> = this.authService.isAuthorized([
        PermissionType.AccessProductFamilies,
    ]);

    private countRequested = false;

    public readonly canCreateParts = computed(() => this.isAuthorized() && this.hasProductFamily());

    constructor() {
        // Permissions are restored from storage before the auth exchange of this page load answers,
        // and the token kept next to them may already be expired, so the count waits for the
        // exchange instead of for the permissions alone.
        effect(() => {
            if (!this.authService.sessionValidated() || !this.isAuthorized()
                || !this.canAccessProductFamilies() || this.countRequested) {
                return;
            }

            this.countRequested = true;
            this.productFamilyService.countProductFamilies().subscribe({
                next: count => this.hasProductFamily.set(count > 0),
                // A failed count must not latch the entry point off for the rest of the session.
                error: () => this.countRequested = false,
            });
        });
    }
}
